import React, {useCallback} from "react"
import PropTypes from "prop-types"
import {Box, InputAdornment, TextField, Typography} from "@mui/material"
import InfoIcon from "../../components/general/infoIcon"
import {getMerkmal} from "../../misc/widget-functions";
import {getEventValue} from "../../misc/utils";

const KEY_CODE_RETURN_OR_ENTER = 13

const BaseTextInput = ({
    widget,
    triggerAction,
    onBlurValidations,
    onChangeValidations,
    transformation,
    onBlur,
    onValueChange: onChange,
    selectOnFocus,
    onFocus,
    multiline,
    rows,
    label,
    value,
    disabled: disabledWidget,
    placeholder,
    error,
    helperText,
    autoFocus,
    inputMode,
    inputProps,
    endAdornment,
    inputComponent,
    sx,
    type
}) => {
    const disabled = disabledWidget || !getMerkmal(widget)?.active
    const localOnChange = useCallback(
        (event) => {
            const eventValue = getEventValue(event)
            if (onChange) {
                onChange(eventValue, transformation, onChangeValidations)
            }
        },
        [onChange, transformation, onChangeValidations],
    )

    const localOnBlur = useCallback(
        (event) => {
            const eventValue = getEventValue(event)
            if (onBlur) {
                onBlur(eventValue, transformation, onBlurValidations)
            }
        },
        [onBlur, transformation, onBlurValidations],
    )

    const localOnFocus = useCallback(
        (event) => {
            if (selectOnFocus) {
                event.target.select()
            }
            if (onFocus) {
                onFocus(event)
            }
        },
        [onFocus, selectOnFocus],
    )

    const localOnEnter = useCallback(
        (event) => {
            if (event.keyCode === KEY_CODE_RETURN_OR_ENTER && !multiline) {
                event.target.blur()
            }
        },
        [multiline],
    )

    return (
        <Box>
            {widget.text && (
                <Box
                    display="flex"
                    alignItems="center">
                    <Typography className={disabled ? "disabled" : ""}>
                        {widget.text}
                    </Typography>
                </Box>
            )}
            <Box
                gap="4px"
                display="flex"
                alignItems="center">
                <TextField
                    type={type ?? "text"}
                    multiline
                    rows={rows}
                    label={label}
                    value={value ?? ""}
                    fullWidth
                    onChange={localOnChange}
                    onBlur={localOnBlur}
                    onFocus={localOnFocus}
                    onKeyUp={localOnEnter}
                    disabled={disabled}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    autoFocus={autoFocus}
                    inputProps={{
                        inputMode,
                    }}
                    InputProps={{
                        ...(inputProps && {inputProps}),
                        ...(endAdornment
                            ? {
                                  endAdornment: (
                                      <InputAdornment position="end">
                                          {endAdornment}
                                      </InputAdornment>
                                  ),
                              }
                            : {}),
                        inputComponent,
                    }}
                    sx={sx}
                /><InfoIcon infoText={widget.infotext} />

                {widget.subtext && (
                    <Typography className={disabled ? "disabled" : ""}>
                        {widget.subtext}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

BaseTextInput.propTypes = {
    onBlurValidations: PropTypes.array,
    onChangeValidations: PropTypes.array,
    transformation: PropTypes.func,
    onChange: PropTypes.any,
    onBlur: PropTypes.any,
    onFocus: PropTypes.any,
    selectOnFocus: PropTypes.bool,
    multiline: PropTypes.bool,
    rows: PropTypes.number,
    label: PropTypes.string,
    value: PropTypes.any,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    placeholder: PropTypes.string,
    helperText: PropTypes.any,
    autoFocus: PropTypes.bool,
    inputMode: PropTypes.string,
    inputProps: PropTypes.object,
    endAdornment: PropTypes.string,
    inputComponent: PropTypes.any,
}

BaseTextInput.defaultProps = {
    onBlurValidations: [],
    onChangeValidations: [],
    transformation: (x) => x,
    onChange: null,
    onBlur: null,
    onFocus: null,
    selectOnFocus: false,
    multiline: false,
    rows: 4,
    label: "",
    value: null,
    disabled: false,
    error: false,
    placeholder: "",
    helperText: null,
    autoFocus: false,
    inputMode: "text",
    endAdornment: "",
    inputProps: {},
    inputComponent: null,
}

export default BaseTextInput
